import { supabase } from '../../lib/supabase'
import { sportsStatsService } from '../sports/sportsStatsService'
import type { StatsFilters } from '../../types/sportsStats'

export interface DashboardContextResponse {
  collective: boolean
  athletes: Array<{
    id: string
    first_name: string
    last_name: string
    primary_position: string
    dorsal_number?: number
    tracking_status?: string
  }>
  camps?: Array<{
    id: string
    title: string
    start_time: string
    end_time: string
  }>
  seasons?: Array<{
    id: string
    name: string
    is_current?: boolean
  }>
}

export interface AvailabilityData {
  as_of?: string
  counts: {
    available?: number
    needs_assessment?: number
    limited?: number
    not_assessed?: number
    unavailable?: number
    total?: number
  }
  rows: Array<{
    athlete_id: string
    first_name?: string
    last_name?: string
    status: 'available' | 'needs_assessment' | 'limited' | 'not_assessed' | 'unavailable'
    review_pending?: boolean
    valid_from?: string
    valid_to?: string
    assessed_at?: string
    review_at?: string
    notes?: string
    restriction?: string
    source_id?: string
  }>
}


export interface WorkloadData {
  weeks: Array<{
    week_start: string
    load: number
    sessions: number
    days: number
    mean?: number
    min?: number
    max?: number
  }>
  unconfirmed: number
  rows?: Array<{
    id: string
    athlete_id: string
    session_date: string
    load: number
    activity_type: string
    rpe: number
    duration_minutes: number
    confirmed_participation: boolean
  }>
}

export interface ComplianceData {
  weeks: Array<{
    week_start: string
    expected: number
    submitted: number
    drafts: number
    pending: number
    rate: number | null
  }>
  rows: Array<{
    athlete_id: string
    status: 'submitted' | 'late_submitted' | 'draft' | 'pending' | 'exempt'
    period_start: string
    source_id?: string
  }>
}

export interface HealthData {
  responses: Array<{
    athlete_id: string
    period_start: string
    version: string
    payload: {
      fatigue?: number
      recovery?: number
      muscle_soreness?: number
      stress?: number
      mood?: number
      sleep_hours?: number
      sleep_quality?: number
      nutrition_quality?: number
      water_intake?: string
      [key: string]: unknown
    }
  }>
  zones: Array<{
    zone: string
    label: string
    people: number
    episodes: number
    severity_mean?: number
  }>
  distributions?: Array<{
    indicator: string
    value: string
    count: number
  }>
}

export interface ConversionStats {
  attempts_1pt: number
  made_1pt: number
  pct_1pt: number | null
  attempts_2pt: number
  made_2pt: number
  pct_2pt: number | null
  total_attempts: number
  total_made: number
  total_pct: number | null
  total_points: number
  snaps: Array<{
    id: string
    sequence: number
    conversion_points_attempted: 1 | 2
    conversion_result: 'successful' | 'failed' | 'pending'
    primary_result: string | null
    points_awarded: number
    yard_line_start: number | null
    preceding_touchdown_snap_id: string | null
    passer_athlete_id: string | null
    receiver_athlete_id: string | null
    passer_name?: string
    receiver_name?: string
    registered_at: string
  }>
  by_player: Array<{
    athlete_id: string
    name: string
    attempts: number
    made: number
    points: number
  }>
}

export interface ConnectionPair {
  passer_id: string
  passer_name: string
  receiver_id: string
  receiver_name: string
  targets: number
  completions: number
  comp_pct: number | null
  yards: number
  tds: number
  concepts: Record<string, number>
}

export interface PlayerCoincidenceStats {
  athlete_id: string
  athlete_name: string
  with_player: {
    snaps: number
    success_rate: number | null
    yards_per_snap: number | null
    tds: number
    turnovers: number
  }
  without_player: {
    snaps: number
    success_rate: number | null
    yards_per_snap: number | null
    tds: number
    turnovers: number
  }
}

export interface EvaluationCompareResult {
  cohort: {
    paired: number
    before_measured: number
    after_measured: number
    mean_difference: number | null
  }
  rows: Array<{
    athlete_id: string
    athlete_name?: string
    left_value: number | null
    right_value: number | null
    difference: number | null
    percent_change: number | null
    comparable: boolean
    warning?: string
  }>
}

/**
 * Calcula el periodo para consultas del Dashboard según el ajuste preestablecido.
 * Cumple que p_from <= p_to, p_to - p_from <= 366 y p_to <= current_date.
 */
export type PeriodPreset = 'last_8_weeks' | 'camp_interval' | 'all_history' | 'custom'

export function getDefaultDashboardPeriod(
  preset: PeriodPreset | number = 'last_8_weeks',
  options?: {
    customFrom?: string
    customTo?: string
    campFrom?: string
    campTo?: string
  }
): { from: string; to: string } {
  const now = new Date()
  const todayStr = now.toISOString().slice(0, 10)

  if (typeof preset === 'number') {
    const fromDate = new Date(now.getTime() - preset * 24 * 60 * 60 * 1000)
    return { from: fromDate.toISOString().slice(0, 10), to: todayStr }
  }

  if (preset === 'camp_interval' && options?.campFrom) {
    const from = options.campFrom.slice(0, 10)
    let to = (options.campTo || options.campFrom).slice(0, 10)
    if (to > todayStr) to = todayStr
    if (from <= to) {
      return { from, to }
    }
  }

  if (preset === 'all_history') {
    // Todo el histórico de la temporada (desde 2026-01-01 hasta hoy, o máx 365 días)
    const fromDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
    const yearStart = `${now.getUTCFullYear()}-01-01`
    const from = yearStart < fromDate.toISOString().slice(0, 10) ? fromDate.toISOString().slice(0, 10) : yearStart
    return { from, to: todayStr }
  }

  if (preset === 'custom' && options?.customFrom && options?.customTo) {
    let from = options.customFrom.slice(0, 10)
    let to = options.customTo.slice(0, 10)
    if (to > todayStr) to = todayStr
    if (from > to) from = to
    return { from, to }
  }

  // Por defecto: últimas 8 semanas (56 días)
  const fromDate = new Date(now.getTime() - 56 * 24 * 60 * 60 * 1000)
  const from = fromDate.toISOString().slice(0, 10)
  return { from, to: todayStr }
}



export const dashboardService = {
  /**
   * Obtiene los equipos disponibles para el usuario en el dashboard
   */
  async getTeams(): Promise<Array<{ id: string; name: string }>> {
    if (!supabase) return []
    const { data, error } = await supabase.rpc('dashboard_teams')
    if (error) {
      console.warn('Error fetching dashboard_teams:', error)
      return []
    }
    return (data as any) || []
  },

  /**
   * Obtiene el registro de origen de un KPI del dashboard (drill-down auditado)
   */
  async getRecord(teamId: string, type: 'compliance' | 'training' | 'form', id: string): Promise<any> {
    if (!supabase) return null
    const { data, error } = await supabase.rpc('dashboard_record', {
      p_team: teamId,
      p_type: type,
      p_id: id,
    })
    if (error) {
      console.warn('Error fetching dashboard_record:', error)
      return null
    }
    return data
  },

  /**
   * Obtiene el contexto organizativo y de equipo para el dashboard
   */
  async getContext(params: {
    teamId: string
    from?: string
    to?: string
    inactive?: boolean
    camp?: string
    athlete?: string
  }): Promise<DashboardContextResponse> {
    if (!supabase) throw new Error('Backend no configurado')
    const period = getDefaultDashboardPeriod()
    const from = params.from || period.from
    const to = params.to || period.to

    const { data, error } = await supabase.rpc('dashboard_context', {
      p_team: params.teamId,
      p_from: from,
      p_to: to,
      p_inactive: params.inactive ?? false,
      p_camp: params.camp || null,
      p_athlete: params.athlete || null,
    })
    if (error) {
      console.warn('Error en dashboard_context:', error.message)
      throw error
    }
    return data as DashboardContextResponse
  },

  /**
   * Obtiene datos específicos por bloque con RLS estricto
   */
  async getData<T>(
    block: 'availability' | 'workload' | 'compliance' | 'health' | 'body' | 'performance',
    params: {
      teamId: string
      from?: string
      to?: string
      inactive?: boolean
      athlete?: string
      camp?: string
    }
  ): Promise<T | null> {
    if (!supabase) return null
    const period = getDefaultDashboardPeriod()
    const from = params.from || period.from
    const to = params.to || period.to

    try {
      const { data, error } = await supabase.rpc('dashboard_data', {
        p_team: params.teamId,
        p_block: block,
        p_from: from,
        p_to: to,
        p_inactive: params.inactive ?? false,
        p_athlete: params.athlete || null,
        p_camp: params.camp || null,
      })
      if (error) {
        // Si es 403 o permiso denegado, retornar null sin quebrar la pantalla
        if (error.code === '42501' || error.message.includes('No autorizado') || error.message.includes('FORBIDDEN')) {
          console.info(`Acceso restringido a bloque ${block}:`, error.message)
          return null
        }
        throw error
      }
      return data as T
    } catch (err: unknown) {
      console.warn(`Error cargando bloque ${block}:`, err instanceof Error ? err.message : err)
      return null
    }
  },

  /**
   * Compara dos evaluaciones longitudinales compatibles
   */
  async compareEvaluations(params: {
    teamId: string
    kind: 'body' | 'performance'
    leftSession: string
    rightSession: string
    code: string
    side?: string
    from?: string
    to?: string
    camp?: string
    athlete?: string
    inactive?: boolean
  }): Promise<EvaluationCompareResult | null> {
    if (!supabase) return null
    // Ventana longitudinal anual por defecto (hasta 365 días)
    const period = getDefaultDashboardPeriod(365)
    const from = params.from || period.from
    const to = params.to || period.to

    try {
      const { data, error } = await supabase.rpc('dashboard_evaluation_compare', {
        p_team: params.teamId,
        p_from: from,
        p_to: to,
        p_kind: params.kind,
        p_left: params.leftSession,
        p_right: params.rightSession,
        p_code: params.code,
        p_side: params.side || 'none',
        p_camp: params.camp || null,
        p_athlete: params.athlete || null,
        p_inactive: params.inactive ?? false,
      })
      if (error) {
        console.warn('Error en dashboard_evaluation_compare:', error.message)
        return null
      }
      return data as EvaluationCompareResult
    } catch (err) {
      console.warn('Fallo en compareEvaluations:', err)
      return null
    }
  },

  /**
   * Estadísticas de conversiones de 1 y 2 puntos extraídas de la tabla snaps real
   */
  async getConversionStats(teamId: string, captureId?: string): Promise<ConversionStats> {
    if (!supabase) {
      return {
        attempts_1pt: 0,
        made_1pt: 0,
        pct_1pt: null,
        attempts_2pt: 0,
        made_2pt: 0,
        pct_2pt: null,
        total_attempts: 0,
        total_made: 0,
        total_pct: null,
        total_points: 0,
        snaps: [],
        by_player: [],
      }
    }

    try {
      let query = supabase
        .from('snaps')
        .select(`
          id,
          sequence,
          conversion_points_attempted,
          conversion_result,
          primary_result,
          points_awarded,
          yard_line_start,
          preceding_touchdown_snap_id,
          passer_athlete_id,
          receiver_athlete_id,
          registered_at
        `)
        .eq('team_id', teamId)
        .eq('is_conversion', true)
        .order('sequence', { ascending: true })

      if (captureId) {
        query = query.eq('capture_id', captureId)
      }

      const { data, error } = await query
      if (error) {
        console.warn('Error al cargar snaps de conversión:', error.message)
        throw error
      }

      const snaps = (data || []) as any[]

      // Obtener nombres de atletas para enriquecer
      const athleteIds = Array.from(
        new Set(
          snaps
            .flatMap((s) => [s.passer_athlete_id, s.receiver_athlete_id])
            .filter(Boolean)
        )
      )

      let athleteMap: Record<string, string> = {}
      if (athleteIds.length > 0) {
        const { data: athletes } = await supabase
          .from('athletes')
          .select('id, first_name, last_name')
          .in('id', athleteIds)
        if (athletes) {
          athleteMap = Object.fromEntries(
            athletes.map((a: { id: string; first_name: string; last_name: string }) => [a.id, `${a.first_name} ${a.last_name}`.trim()])
          )
        }
      }

      let att1 = 0, made1 = 0
      let att2 = 0, made2 = 0
      const playerMap: Record<string, { attempts: number; made: number; points: number; name: string }> = {}

      const mappedSnaps = snaps.map((s) => {
        const ptsAttempted = (s.conversion_points_attempted || 1) as 1 | 2
        const isMade = s.conversion_result === 'successful' || s.primary_result === 'conversion_good'
        const ptsAwarded = s.points_awarded ?? (isMade ? ptsAttempted : 0)

        if (ptsAttempted === 1) {
          att1++
          if (isMade) made1++
        } else if (ptsAttempted === 2) {
          att2++
          if (isMade) made2++
        }

        const actorId = s.receiver_athlete_id || s.passer_athlete_id
        if (actorId) {
          const name = athleteMap[actorId] || 'Atleta'
          if (!playerMap[actorId]) {
            playerMap[actorId] = { attempts: 0, made: 0, points: 0, name }
          }
          playerMap[actorId].attempts++
          if (isMade) {
            playerMap[actorId].made++
            playerMap[actorId].points += ptsAwarded
          }
        }

        return {
          id: s.id,
          sequence: s.sequence,
          conversion_points_attempted: ptsAttempted,
          conversion_result: s.conversion_result || (isMade ? 'successful' : 'failed'),
          primary_result: s.primary_result,
          points_awarded: ptsAwarded,
          yard_line_start: s.yard_line_start,
          preceding_touchdown_snap_id: s.preceding_touchdown_snap_id,
          passer_athlete_id: s.passer_athlete_id,
          receiver_athlete_id: s.receiver_athlete_id,
          passer_name: s.passer_athlete_id ? athleteMap[s.passer_athlete_id] : undefined,
          receiver_name: s.receiver_athlete_id ? athleteMap[s.receiver_athlete_id] : undefined,
          registered_at: s.registered_at,
        }
      })

      const totalAtt = att1 + att2
      const totalMade = made1 + made2
      const totalPts = made1 * 1 + made2 * 2

      return {
        attempts_1pt: att1,
        made_1pt: made1,
        pct_1pt: att1 > 0 ? Math.round((made1 / att1) * 100) : null,
        attempts_2pt: att2,
        made_2pt: made2,
        pct_2pt: att2 > 0 ? Math.round((made2 / att2) * 100) : null,
        total_attempts: totalAtt,
        total_made: totalMade,
        total_pct: totalAtt > 0 ? Math.round((totalMade / totalAtt) * 100) : null,
        total_points: totalPts,
        snaps: mappedSnaps,
        by_player: Object.entries(playerMap).map(([id, stats]) => ({
          athlete_id: id,
          name: stats.name,
          attempts: stats.attempts,
          made: stats.made,
          points: stats.points,
        })),
      }
    } catch (err) {
      console.warn('Error calculando conversiones:', err)
      return {
        attempts_1pt: 0,
        made_1pt: 0,
        pct_1pt: null,
        attempts_2pt: 0,
        made_2pt: 0,
        pct_2pt: null,
        total_attempts: 0,
        total_made: 0,
        total_pct: null,
        total_points: 0,
        snaps: [],
        by_player: [],
      }
    }
  },

  /**
   * Conexiones pasador-receptor reales (BK-05)
   */
  async getConnections(teamId: string, filters: StatsFilters = {}): Promise<ConnectionPair[]> {
    if (!supabase) return []
    try {
      let query = supabase
        .from('snaps')
        .select(`
          id,
          passer_athlete_id,
          receiver_athlete_id,
          primary_result,
          net_yards,
          is_touchdown,
          offense_concept_name,
          validity
        `)
        .eq('team_id', teamId)
        .eq('validity', 'valid')
        .in('primary_result', ['pass_complete', 'pass_incomplete', 'interception'])
        .not('passer_athlete_id', 'is', null)
        .not('receiver_athlete_id', 'is', null)

      if (filters.captureId) query = query.eq('capture_id', filters.captureId)
      if ((filters as any).conceptId) query = query.eq('offense_concept_id', (filters as any).conceptId)

      const { data, error } = await query
      if (error || !data) return []

      const pairMap: Record<string, {
        passer_id: string
        receiver_id: string
        targets: number
        completions: number
        yards: number
        tds: number
        concepts: Record<string, number>
      }> = {}

      for (const row of data as any[]) {
        // En Flag 5v5 una conexión pasador-receptor representa una interacción entre dos jugadores distintos.
        // Se descartan registros donde el pasador figura como receptor (p. ej. carreras mal etiquetadas o deflections no intencionales).
        if (row.passer_athlete_id === row.receiver_athlete_id) continue

        const key = `${row.passer_athlete_id}:${row.receiver_athlete_id}`
        if (!pairMap[key]) {
          pairMap[key] = {
            passer_id: row.passer_athlete_id,
            receiver_id: row.receiver_athlete_id,
            targets: 0,
            completions: 0,
            yards: 0,
            tds: 0,
            concepts: {},
          }
        }
        const p = pairMap[key]
        p.targets++
        const isComplete = row.primary_result === 'pass_complete' || row.is_touchdown
        if (isComplete) {
          p.completions++
          p.yards += row.net_yards || 0
        }
        if (row.is_touchdown) {
          p.tds++
        }
        const concept = row.offense_concept_name || 'Standard'
        p.concepts[concept] = (p.concepts[concept] || 0) + 1
      }

      // Obtener nombres
      const athleteIds = Array.from(
        new Set(
          Object.values(pairMap).flatMap((p) => [p.passer_id, p.receiver_id])
        )
      )

      let nameMap: Record<string, string> = {}
      if (athleteIds.length > 0) {
        const { data: athletes } = await supabase
          .from('athletes')
          .select('id, first_name, last_name')
          .in('id', athleteIds)
        if (athletes) {
          nameMap = Object.fromEntries(
            athletes.map((a: { id: string; first_name: string; last_name: string }) => [a.id, `${a.first_name} ${a.last_name}`.trim()])
          )
        }
      }

      return Object.values(pairMap).map((p) => ({
        passer_id: p.passer_id,
        passer_name: nameMap[p.passer_id] || 'Pasador',
        receiver_id: p.receiver_id,
        receiver_name: nameMap[p.receiver_id] || 'Receptor',
        targets: p.targets,
        completions: p.completions,
        comp_pct: p.targets > 0 ? Math.round((p.completions / p.targets) * 100) : null,
        yards: p.yards,
        tds: p.tds,
        concepts: p.concepts,
      })).sort((a, b) => b.targets - a.targets)
    } catch (err) {
      console.warn('Error calculando conexiones:', err)
      return []
    }
  },

  /**
   * Coincidencia en campo centrada en un jugador (BK-06)
   */
  async getPlayerCoincidence(teamId: string, athleteId: string, filters: StatsFilters = {}): Promise<PlayerCoincidenceStats | null> {
    if (!supabase) return null
    try {
      // 1. Obtener todos los snaps válidos del equipo
      let snapQuery = supabase
        .from('snaps')
        .select(`
          id,
          net_yards,
          is_touchdown,
          is_first_down,
          is_turnover,
          primary_result
        `)
        .eq('team_id', teamId)
        .eq('validity', 'valid')

      if (filters.captureId) snapQuery = snapQuery.eq('capture_id', filters.captureId)

      const { data: allSnaps, error: snapErr } = await snapQuery
      if (snapErr || !allSnaps) return null

      // 2. Obtener los snap_ids en los que participó el atleta
      const { data: partData, error: partErr } = await supabase
        .from('snap_participants')
        .select('snap_id')
        .eq('team_id', teamId)
        .eq('athlete_id', athleteId)

      if (partErr) return null

      const participatedSnapIds = new Set((partData || []).map((p: { snap_id: string }) => p.snap_id))

      let withCount = 0, withYards = 0, withSuccess = 0, withTds = 0, withTo = 0
      let withoutCount = 0, withoutYards = 0, withoutSuccess = 0, withoutTds = 0, withoutTo = 0

      for (const s of allSnaps as any[]) {
        const participated = participatedSnapIds.has(s.id)
        const isSuccess = s.is_first_down || s.is_touchdown || (s.net_yards && s.net_yards >= 4)
        const yards = s.net_yards || 0

        if (participated) {
          withCount++
          withYards += yards
          if (isSuccess) withSuccess++
          if (s.is_touchdown) withTds++
          if (s.is_turnover) withTo++
        } else {
          withoutCount++
          withoutYards += yards
          if (isSuccess) withoutSuccess++
          if (s.is_touchdown) withoutTds++
          if (s.is_turnover) withoutTo++
        }
      }

      // Nombre del atleta
      const { data: athlete } = await supabase
        .from('athletes')
        .select('first_name, last_name')
        .eq('id', athleteId)
        .single()

      const athleteName = athlete ? `${athlete.first_name} ${athlete.last_name}`.trim() : 'Atleta'

      return {
        athlete_id: athleteId,
        athlete_name: athleteName,
        with_player: {
          snaps: withCount,
          success_rate: withCount > 0 ? Math.round((withSuccess / withCount) * 100) : null,
          yards_per_snap: withCount > 0 ? Number((withYards / withCount).toFixed(1)) : null,
          tds: withTds,
          turnovers: withTo,
        },
        without_player: {
          snaps: withoutCount,
          success_rate: withoutCount > 0 ? Math.round((withoutSuccess / withoutCount) * 100) : null,
          yards_per_snap: withoutCount > 0 ? Number((withoutYards / withoutCount).toFixed(1)) : null,
          tds: withoutTds,
          turnovers: withoutTo,
        },
      }
    } catch (err) {
      console.warn('Error calculando coincidencia de jugador:', err)
      return null
    }
  },

  // Reexportar helpers de estadísticas deportivas para evitar duplicar lógica
  getOverview: sportsStatsService.getOverview,
  getAllPlayers: sportsStatsService.getAllPlayers,
  getLineups: sportsStatsService.getLineups,
  getOffenseBreakdown: sportsStatsService.getOffenseBreakdown,
  getDefenseBreakdown: sportsStatsService.getDefenseBreakdown,
  getPlayerDetail: sportsStatsService.getPlayerDetail,
  getFilters: sportsStatsService.getFilters,
}
